import { DietType, getDietType, type Traits, type Vec2, type Obstacle } from './types'
import type { Food } from './food'

let nextId = 0

export const MAX_ENERGY = 250
const BASE_ENERGY = 80

/** 乱数ヘルパー */
function rand(min: number, max: number): number {
  return min + Math.random() * (max - min)
}

/** 食性スコアをランダムに生成（草食寄り55%、肉食寄り25%、雑食寄り20%の初期分布） */
function randDietScore(): number {
  const r = Math.random()
  if (r < 0.55) return rand(0.7, 1.0)   // 草食傾向
  if (r < 0.80) return rand(0.0, 0.3)   // 肉食傾向
  return rand(0.3, 0.7)                  // 雑食傾向
}

export function randomTraits(): Traits {
  const dietScore = randDietScore()
  const diet = getDietType(dietScore)
  const speedBase = diet === DietType.Carnivore ? 2.5 : diet === DietType.Herbivore ? 1.5 : 2.0
  // 肉食は視野が広い（獲物を探す必要があるため）
  const visionBase = diet === DietType.Carnivore ? rand(80, 160)
    : diet === DietType.Omnivore ? rand(60, 130) : rand(40, 100)
  return {
    speed: rand(speedBase * 0.7, speedBase * 1.3),
    visionRange: visionBase,
    size: rand(
      diet === DietType.Carnivore ? 5 : 3,
      diet === DietType.Carnivore ? 9 : 6,
    ),
    dietScore,
    hue: diet === DietType.Herbivore ? rand(90, 150) : diet === DietType.Carnivore ? rand(0, 30) : rand(200, 280),
    metabolismRate: rand(0.8, 1.2),
    // 草食は繁殖力を高めに設定（捕食者に捕まりやすい分、個体数を維持しやすくする）
    fertilityRate: diet === DietType.Herbivore ? rand(1.3, 1.8)
      : diet === DietType.Omnivore ? rand(0.6, 0.9) : rand(0.8, 1.2),
  }
}

export class Organism {
  id: number
  pos: Vec2
  vel: Vec2
  energy: number
  age: number
  alive: boolean
  traits: Traits
  /** 世代番号（0が初期個体） */
  generation: number
  /** 親個体のID（初期個体はnull） */
  parentId: number | null

  /** 最大寿命（秒）: この年齢に達したら死滅 */
  maxLifespan: number

  private wanderAngle: number
  private stateTimer: number
  /** 獲物が見つからなかった連続フレーム数 */
  private starvingTicks: number
  /** 捕食クールダウン（キル後に次の捕食まで待機するフレーム数） */
  private huntCooldown: number
  /** 誕生クールダウン（生まれた直後は繁殖不可。0になると繁殖可能） */
  birthCooldown: number

  constructor(
    x: number,
    y: number,
    traits?: Traits,
    generation = 0,
    parentId: number | null = null,
  ) {
    this.id = nextId++
    this.pos = { x, y }
    this.vel = { x: 0, y: 0 }
    this.energy = BASE_ENERGY
    this.age = 0
    this.alive = true
    this.traits = traits ?? randomTraits()
    this.wanderAngle = Math.random() * Math.PI * 2
    this.stateTimer = 0
    this.generation = generation
    this.parentId = parentId
    this.starvingTicks = 0
    this.huntCooldown = 0
    this.birthCooldown = 0  // 初期個体はクールダウンなし（reproduce()で設定される）
    // 寿命: 15〜45秒（個体差あり）
    this.maxLifespan = 15 + Math.random() * 30
  }

  update(
    dt: number,
    foods: Food[],
    organisms: Organism[],
    worldW: number,
    worldH: number,
    obstacles: Obstacle[],
  ): void {
    this.age += dt

    // 寿命チェック: 最大寿命に達したら死滅
    if (this.age >= this.maxLifespan) {
      this.alive = false
      return
    }

    // 変更2: 連続代謝コスト（肉食ほど高コスト: 捕食行動・高速移動の維持費）
    const dietMultiplier = 1.5 - this.traits.dietScore * 0.75
    // 純肉食(ds=0): 1.5x, 雑食(ds=0.5): 1.125x, 純草食(ds=1): 0.75x
    // サイズコストを非線形化: サイズ5超で急激にコスト増（サイズ暴走を抑制）
    const s = this.traits.size
    const sizeCost = s <= 5 ? s * 0.1 : s * 0.1 + Math.pow(s - 5, 1.5) * 0.02
    const drain = 0.008 * this.traits.metabolismRate * dietMultiplier
      * (1 + this.traits.speed * 0.5 + sizeCost)
    this.energy -= drain * dt * 60

    // クールダウンの消化
    if (this.huntCooldown > 0) this.huntCooldown--
    if (this.birthCooldown > 0) this.birthCooldown--

    // 全個体で飢餓カウンターを進める（食べた時だけリセット）
    this.starvingTicks++

    // 連続飢餓ペナルティ（全個体に適用。dietScoreに基づく連続値で強度を調整）
    const ds = this.traits.dietScore
    if (ds < 0.5) {
      // 肉食側: 純肉食(t=0)ほどonset早く・rate高く・急速に衰弱
      const t = ds / 0.5 // 0=純肉食, 1=完全雑食
      const onset = 80 + t * 30       // 80-110
      const rate = 0.06 - t * 0.015   // 0.06-0.045
      const maxEsc = 2.0 - t * 0.2    // 2.0-1.8
      const window = 180 + t * 20     // 180-200
      if (this.starvingTicks > onset) {
        const esc = 1 + Math.min((this.starvingTicks - onset) / window, maxEsc)
        this.energy -= rate * esc * dt * 60
      }
    } else {
      // 草食側: 植物食に特化した消化器官を持つが、食べなければ衰弱する
      // 純草食(t=1)ほどonset遅く・rate低い（食料を見つけやすいが過密時は競合で飢える）
      const t = (ds - 0.5) / 0.5 // 0=完全雑食, 1=純草食
      const onset = 150 + t * 60    // 150-210 ticks（肉食より長い猶予）
      const rate = 0.030 - t * 0.010 // 0.030-0.020/tick
      const maxEsc = 1.6 - t * 0.3  // 1.6-1.3
      const window = 200 + t * 50   // 200-250
      if (this.starvingTicks > onset) {
        const esc = 1 + Math.min((this.starvingTicks - onset) / window, maxEsc)
        this.energy -= rate * esc * dt * 60
      }
    }

    if (this.energy <= 0) {
      this.alive = false
      return
    }

    // 草食傾向（dietScore >= 0.5）の個体は捕食者を検知して逃避
    if (this.traits.dietScore >= 0.5) {
      const predator = this.detectPredator(organisms)

      if (predator) {
        const dx = this.pos.x - predator.x
        const dy = this.pos.y - predator.y
        const len = Math.sqrt(dx * dx + dy * dy) || 1
        const spd = this.traits.speed * 60 * 1.0  // 仕様E-4: 逃走速度ブースト撤廃（1.1→1.0）
        this.vel.x = (dx / len) * spd
        this.vel.y = (dy / len) * spd
        this.pos.x += this.vel.x * dt
        this.pos.y += this.vel.y * dt
        this.resolveObstacles(obstacles)
        this.pos.x = ((this.pos.x % worldW) + worldW) % worldW
        this.pos.y = ((this.pos.y % worldH) + worldH) % worldH
        this.eat(foods, organisms)
        if (this.energy > MAX_ENERGY) this.energy = MAX_ENERGY
        this.stateTimer += dt
        return
      }
    }

    const target = this.findTarget(foods, organisms)

    if (target) {
      this.moveToward(target, dt)
    } else {
      this.wander(dt)
    }

    this.resolveObstacles(obstacles)
    this.pos.x = ((this.pos.x % worldW) + worldW) % worldW
    this.pos.y = ((this.pos.y % worldH) + worldH) % worldH
    this.eat(foods, organisms)
    if (this.energy > MAX_ENERGY) this.energy = MAX_ENERGY
    this.stateTimer += dt
  }

  /** 変更5: 近くの捕食者を検出（predationAbility > 0.2 を捕食者とみなす） */
  private detectPredator(organisms: Organism[]): Vec2 | null {
    const vr = this.traits.visionRange * 1.0  // 仕様E-4: 捕食者検知ボーナス撤廃
    let closest: Vec2 | null = null
    let closestDist = vr

    for (const o of organisms) {
      if (!o.alive || o.id === this.id) continue
      // シグモイド捕食能力で捕食者を判定（0.2以下は無害とみなす）
      const predAbility = 1 / (1 + Math.exp(15 * (o.traits.dietScore - 0.5)))
      if (predAbility <= 0.2) continue
      const d = dist(this.pos, o.pos)
      if (d < closestDist) {
        closestDist = d
        closest = o.pos
      }
    }
    return closest
  }

  /**
   * 捕食可能な獲物か判定
   * - dietScore >= 0.85: 捕食不可
   * - dietScore < 0.2（強い肉食）: 草食は体格優位で可、その他はサイズ65%以下
   * - dietScore 0.2〜0.85（中〜弱の肉食）: 連続サイズ制限（フロア30%）
   */
  private isValidPrey(prey: Organism): boolean {
    const { dietScore, size } = this.traits
    // 仕様B: ゲート緩和（0.5→0.85）雑食寄りでも捕食可能に
    if (dietScore >= 0.85) return false

    const od = prey.traits.dietScore
    const os = prey.traits.size

    if (dietScore < 0.2) {
      // 強い肉食傾向: 草食は体格優位があれば可、それ以外はサイズ制限
      if (od >= 0.7) return size > os * 0.55
      return os <= size * 0.65
    }

    // 仕様K: dietScore 0.2〜0.85の連続サイズ制限
    // 「飢えれば何でも口に入れる」フロア0.30保証
    const sizeRatio = Math.max(0.30, 1.1 - 1.2 * dietScore)
    return os <= size * sizeRatio
  }

  private findTarget(foods: Food[], organisms: Organism[]): Vec2 | null {
    const { visionRange, dietScore } = this.traits
    let best: Vec2 | null = null
    let bestDist = visionRange

    // 満腹時は徘徊（捕食後の過剰狩猟を抑制）
    if (this.energy > MAX_ENERGY * 0.8) return null

    // 食性ハードゲート: 肉食85%以上（ds <= 0.15）は植物を食べられない
    if (dietScore > 0.15) {
      // 仕様A: ハードカットオフ撤廃＋最低フロア（全食性が植物にアクセス可能、最低5%）
      const plantAccess = Math.max(0.05, 1 / (1 + Math.exp(-15 * (dietScore - 0.5))))
      // 消化効率は最大1.0（エネルギー増幅なし）
      const plantEfficiency = Math.min(1.0, plantAccess * (0.6 + 0.9 * dietScore * dietScore))
      if (plantEfficiency >= 0.01) {
        for (const f of foods) {
          if (!f.alive) continue
          const d = dist(this.pos, f.pos)
          if (d < bestDist) {
            bestDist = d
            best = f.pos
          }
        }
      }
    }

    // 仕様A: ハードカットオフ撤廃＋最低フロア（全食性が捕食にアクセス可能、最低5%）
    const predationAbility = Math.max(0.05, 1 / (1 + Math.exp(15 * (dietScore - 0.5))))
    if (predationAbility >= 0.01) {
      for (const o of organisms) {
        if (!o.alive || o.id === this.id) continue
        if (!this.isValidPrey(o)) continue
        const d = dist(this.pos, o.pos)
        if (d < bestDist) {
          bestDist = d
          best = o.pos
        }
      }
    }

    return best
  }

  private moveToward(target: Vec2, dt: number): void {
    const dx = target.x - this.pos.x
    const dy = target.y - this.pos.y
    const len = Math.sqrt(dx * dx + dy * dy) || 1
    const spd = this.traits.speed * 60
    this.vel.x = (dx / len) * spd
    this.vel.y = (dy / len) * spd
    this.pos.x += this.vel.x * dt
    this.pos.y += this.vel.y * dt
  }

  private wander(dt: number): void {
    this.wanderAngle += (Math.random() - 0.5) * 0.3
    const spd = this.traits.speed * 40
    this.vel.x = Math.cos(this.wanderAngle) * spd
    this.vel.y = Math.sin(this.wanderAngle) * spd
    this.pos.x += this.vel.x * dt
    this.pos.y += this.vel.y * dt
  }

  private resolveObstacles(obstacles: Obstacle[]): void {
    for (const obs of obstacles) {
      const dx = this.pos.x - obs.pos.x
      const dy = this.pos.y - obs.pos.y
      const d = Math.sqrt(dx * dx + dy * dy)
      const minDist = obs.radius + this.traits.size
      if (d < minDist && d > 0) {
        const nx = dx / d
        const ny = dy / d
        this.pos.x = obs.pos.x + nx * minDist
        this.pos.y = obs.pos.y + ny * minDist
        const dot = this.vel.x * nx + this.vel.y * ny
        this.vel.x -= 2 * dot * nx
        this.vel.y -= 2 * dot * ny
      }
    }
  }

  private eat(foods: Food[], organisms: Organism[]): void {
    const { dietScore, size } = this.traits

    // 食性ハードゲート: 肉食85%以上（ds <= 0.15）は植物を食べられない
    if (dietScore > 0.15) {
      // 仕様A: ハードカットオフ撤廃＋最低フロア（全食性が植物にアクセス可能、最低5%）
      const plantAccess = Math.max(0.05, 1 / (1 + Math.exp(-15 * (dietScore - 0.5))))
      // 消化効率は最大1.0（エネルギー増幅なし）
      const plantEfficiency = Math.min(1.0, plantAccess * (0.6 + 0.9 * dietScore * dietScore))
      if (plantEfficiency >= 0.01) {
        for (const f of foods) {
          if (!f.alive) continue
          if (dist(this.pos, f.pos) < size + f.radius) {
            this.energy += f.energy * plantEfficiency
            this.starvingTicks = 0
            f.alive = false
          }
        }
      }
    }

    // 仕様A: ハードカットオフ撤廃＋最低フロア（全食性が捕食にアクセス可能、最低5%）
    const predationAbility = Math.max(0.05, 1 / (1 + Math.exp(15 * (dietScore - 0.5))))
    if (predationAbility >= 0.01) {
      if (this.huntCooldown > 0) return
      for (const o of organisms) {
        if (!o.alive || o.id === this.id) continue
        if (!this.isValidPrey(o)) continue
        if (dist(this.pos, o.pos) < size + o.traits.size) {
          const speedRatio = o.traits.speed / this.traits.speed
          const escapeChance = Math.max(0.05, 1.0 - 0.9 / speedRatio)  // 仕様E-4: 逃走確率さらに緩和（0.7→0.9、狩り成功率アップ）
          if (Math.random() < escapeChance) continue

          const predationRatio = dietScore < 0.2
            ? (o.traits.dietScore >= 0.7 ? 0.55 : 0.35)
            : 0.30
          // 捕食能力シグモイドをエネルギー取得にも乗算（雑食の捕食効率を自然に低下させる）
          this.energy += o.energy * predationRatio * predationAbility
          this.starvingTicks = 0
          o.alive = false
          this.huntCooldown = dietScore < 0.2 ? 60 : 40  // 仕様F: ハントクールダウン短縮（90/60→60/40）
          break
        }
      }
    }
  }
}

function dist(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return Math.sqrt(dx * dx + dy * dy)
}
